import { ValidatorFn } from '@angular/forms';
import { MotifModalConfig } from '@ey-xd/ng-motif';
import {
  ConditionPropertyOperator,
  DateOptions,
  FilterDropdownOption,
  FilterGroup,
  JSONObject,
  PropertyFilter,
  RequestStatus,
  RequestStatusOrder,
  RequestType,
  RequestTypeOrder,
} from '@models';
import { AccessFilterItem, FieldType, FilterCriterion, FilterModalData, FilterModalResult } from '@models/filter.model';
import { getEnumKeyByValue, getEnumValueByKey } from '@utils';

export class FilterHelper {
  private static sortByOrder = (a: string, b: string, enumType: JSONObject, orderEnum: JSONObject): number => {
    const x = getEnumValueByKey(orderEnum, getEnumKeyByValue(enumType, Number(a))?.toString() || '');
    const y = getEnumValueByKey(orderEnum, getEnumKeyByValue(enumType, Number(b))?.toString() || '');
    if (x === y) return 0;
    return Number(x) < Number(y) ? -1 : 1;
  };

  private static sortStatusByOrder = (a: string, b: string) =>
    FilterHelper.sortByOrder(a, b, RequestStatus, RequestStatusOrder);

  private static sortTypeByOrder = (a: string, b: string) =>
    FilterHelper.sortByOrder(a, b, RequestType, RequestTypeOrder);

  static fromModalResultsToFilterGroups(filterModalResults: FilterModalResult[]) {
    const filterGroups: FilterGroup[] = [];
    const filterGroup = {
      logicalGroupOperator: 0,
      entityPropertyFilters: [] as PropertyFilter[],
    };
    filterModalResults.forEach(filterModalResult => {
      switch (filterModalResult.type) {
        case FieldType.MultipleSelect: {
          const filters = {
            logicalGroupOperator: 1,
            entityPropertyFilters: [] as PropertyFilter[],
          };
          switch (filterModalResult.key) {
            case 'requestStatus':
              filterModalResult.values.sort(this.sortStatusByOrder);
              break;
            case 'type':
              filterModalResult.values.sort(this.sortTypeByOrder);
              break;
            case 'classificationType':
            case 'priority':
            case 'status':
              filterModalResult.values.sort((a, b) => Number(a) - Number(b));
              break;
          }

          filterModalResult.values.forEach(value => {
            filters.entityPropertyFilters.push({
              propertyName: filterModalResult.key as string,
              conditionPropertyOperator: filterModalResult.operation,
              value: value,
              field: filterModalResult.field as string,
            });
          });
          if (filters.entityPropertyFilters.length > 0) {
            filterGroups.push(filters);
          }
          break;
        }
        case FieldType.Date: {
          if (filterModalResult.values[0]) {
            filterGroup.entityPropertyFilters.push({
              propertyName: filterModalResult.key as string,
              conditionPropertyOperator: filterModalResult.operation,
              value: filterModalResult.values[0],
              field: filterModalResult.field as string,
            });
          }
          break;
        }
        case FieldType.Text:
        default: {
          const value =
            filterModalResult.operation === ConditionPropertyOperator.Like && filterModalResult.values.length > 0
              ? `%${filterModalResult.values[0]}%`
              : filterModalResult.values[0];

          if (value) {
            filterGroup.entityPropertyFilters.push({
              propertyName: filterModalResult.key as string,
              conditionPropertyOperator: filterModalResult.operation,
              value: value,
              field: filterModalResult.field as string,
            });
          }
          break;
        }
      }
    });
    if (filterGroup.entityPropertyFilters.length > 0) {
      filterGroups.push(filterGroup);
    }

    return filterGroups;
  }

  static getModalConfig(data: FilterModalData): MotifModalConfig<FilterModalData> {
    return {
      width: '500px',
      slideout: true,
      data: data,
    };
  }

  /**
   * Creates a filter criterion object for use in filter UIs and logic.
   *
   * Supports both object-based and positional arguments for backward compatibility.
   *
   * @param keyOrParams - Either:
   *   - An object containing filter criterion properties:
   *     - `key` (string): Unique key for the filter, use when there are more than one filter criterion for the same field e.g. date fields.
   *     - `label` (string): Display label.
   *     - `placeholder` (string): Input placeholder.
   *     - `field` (string): Field name to filter on.
   *     - `type` (FieldType): Field type.
   *     - `operation` (ConditionPropertyOperator): Filter operation.
   *     - `options` (optional): Array of filter options.
   *     - `access` (optional): Array of access filter items.
   *     - `maxLength` (optional): Max input length.
   *     - `validators` (optional): Array of Angular validators.
   *     - `dateOptions` (optional): Date filter options.
   *   - Or, a string key (deprecated, use object form).
   * @param label - Label for the filter (positional, deprecated).
   * @param placeholder - Placeholder for the filter (positional, deprecated).
   * @param field - Field name (positional, deprecated).
   * @param type - Field type (positional, deprecated).
   * @param operation - Filter operation (positional, deprecated).
   * @param options - Filter options (positional, deprecated).
   * @param access - Access filter items (positional, deprecated).
   * @param maxLength - Max input length (positional, deprecated).
   * @param validators - Angular validators (positional, deprecated).
   * @param dateOptions - Date filter options (positional, deprecated).
   * @returns {FilterCriterion} The constructed filter criterion object.
   *
   * @example
   * // Recommended: Object input
   * const criterion = FilterHelper.createFilterCriterion({
   *   key: 'requestId',
   *   label: 'Request ID',
   *   placeholder: 'Search Request ID',
   *   field: 'requestId',
   *   type: FieldType.Text,
   *   operation: ConditionPropertyOperator.Like,
   *   maxLength: 20,
   * });
   *
   * @example
   * // With options and dateOptions
   * const criterion = FilterHelper.createFilterCriterion({
   *   key: 'createdOnFrom',
   *   label: 'Requested Date: From',
   *   placeholder: 'Select Requested Date: From',
   *   field: 'createdOn',
   *   type: FieldType.Date,
   *   operation: ConditionPropertyOperator.OnOrAfter,
   *   dateOptions: { maxDateField: 'createdOnTo' }
   * });
   *
   * @example
   * // Legacy: Positional arguments
   * const criterion = FilterHelper.createFilterCriterion(
   *   'requestId',
   *   'Request ID',
   *   'Search Request ID',
   *   'requestId',
   *   FieldType.Text,
   *   ConditionPropertyOperator.Like,
   *   null,
   *   null,
   *   20
   * );
   */

  static createFilterCriterion(
    keyOrParams:
      | string
      | {
          key?: string;
          label: string;
          placeholder: string;
          field: string;
          type: FieldType;
          operation: ConditionPropertyOperator;
          options?: FilterDropdownOption[];
          access?: AccessFilterItem[];
          maxLength?: number;
          validators?: ValidatorFn[];
          dateOptions?: DateOptions;
        },
    label?: string,
    placeholder?: string,
    field?: string,
    type?: FieldType,
    operation?: ConditionPropertyOperator,
    options?: FilterDropdownOption[],
    access?: AccessFilterItem[],
    maxLength?: number,
    validators?: ValidatorFn[],
    dateOptions?: DateOptions
  ): FilterCriterion {
    if (typeof keyOrParams === 'object') {
      // Handle object input
      const { key, label, placeholder, field, type, operation, options, access, maxLength, validators, dateOptions } =
        keyOrParams;
      return {
        key: key || field,
        label,
        placeholder,
        field,
        type,
        operation,
        ...(options && { options }),
        ...(access && { access }),
        ...(maxLength && { maxLength }),
        ...(validators && { validators }),
        ...(dateOptions && { dateOptions }),
      } as FilterCriterion;
    } else {
      // Handle individual parameters
      return {
        key: keyOrParams || field,
        label: label!,
        placeholder: placeholder!,
        field: field!,
        type: type!,
        operation: operation!,
        ...(options && { options }),
        ...(access && { access }),
        ...(maxLength !== null && { maxLength }),
        ...(validators && { validators }),
        ...(dateOptions && { dateOptions }),
      } as FilterCriterion;
    }
  }
}
